import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  CheckCircle, Clock, Mail, Phone, Users, 
  ArrowRight 
} from 'lucide-react';

const FacilitySignupSuccessPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center text-gray-600 hover:text-gray-900">
              <img 
                src="/logo.gif"
                alt="Addiction Rehab Call Logo" 
                className="h-8 w-auto mr-2"
              />
              <span className="font-medium">Addiction Rehab Call</span>
            </Link>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Success Message */}
          <Card className="text-center mb-8 shadow-lg">
            <CardContent className="py-12">
              <div className="flex justify-center mb-6">
                <div className="bg-green-100 rounded-full p-4">
                  <CheckCircle className="h-12 w-12 text-green-600" />
                </div>
              </div>
              
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                🎉 Registration Submitted Successfully!
              </h1>
              
              <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
                Thank you for joining Addiction Rehab Call! Your facility listing has been submitted 
                for review and you should receive a confirmation email shortly.
              </p>

              <div className="bg-blue-50 rounded-lg p-6 max-w-md mx-auto">
                <div className="flex items-center justify-center mb-3">
                  <Clock className="h-5 w-5 text-blue-600 mr-2" />
                  <span className="font-medium text-blue-900">Review Timeline</span>
                </div>
                <p className="text-blue-700 text-sm">
                  Your listing will be reviewed within <strong>2-3 business days</strong>. 
                  We'll notify you via email once it's approved and live.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Next Steps */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Mail className="h-5 w-5 mr-2 text-blue-600" />
                What Happens Next?
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="bg-blue-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                    <span className="font-bold text-blue-600">1</span>
                  </div>
                  <h4 className="font-medium text-gray-900 mb-2">Review Process</h4>
                  <p className="text-sm text-gray-600">
                    Our team reviews your submission to ensure accuracy and quality standards.
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="bg-green-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                    <span className="font-bold text-green-600">2</span>
                  </div>
                  <h4 className="font-medium text-gray-900 mb-2">Approval & Launch</h4>
                  <p className="text-sm text-gray-600">
                    Once approved, your listing goes live and becomes searchable on our directory.
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="bg-purple-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                    <span className="font-bold text-purple-600">3</span>
                  </div>
                  <h4 className="font-medium text-gray-900 mb-2">Start Receiving Leads</h4>
                  <p className="text-sm text-gray-600">
                    Begin connecting with people actively seeking addiction treatment services.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Lead Generation Offer - HIDDEN FOR REDESIGN */}
          {/* 
          <Card className="bg-gradient-to-br from-green-600 via-blue-600 to-purple-600 text-white mb-8 shadow-lg">
            ... Lead generation content temporarily hidden ...
          </Card>
          */}

          {/* Pricing Preview - HIDDEN FOR REDESIGN */}
          {/*
          <Card className="mb-8">
            ... Pricing content temporarily hidden ...
          </Card>
          */}

          {/* Contact & Support */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Mail className="h-5 w-5 mr-2 text-blue-600" />
                  Need Help?
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Our team is here to help you succeed. Contact us if you have any questions 
                  about your listing or our lead generation services.
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center">
                    <Phone className="h-4 w-4 mr-2 text-gray-400" />
                    <a href="tel:1-888-968-1014" className="text-blue-600 hover:underline">
                      1-888-968-1014
                    </a>
                  </div>
                  <div className="flex items-center">
                    <Mail className="h-4 w-4 mr-2 text-gray-400" />
                    <a href="mailto:facilities@addictionrehabcall.com" className="text-blue-600 hover:underline">
                      facilities@addictionrehabcall.com
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <CheckCircle className="h-5 w-5 mr-2 text-green-600" />
                  Your Account
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Once your facility is approved, you'll receive login credentials to access 
                  your dashboard and manage your listing.
                </p>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/facility-login">
                    Sign In to Dashboard
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FacilitySignupSuccessPage;